import { motion } from "framer-motion";
import { Briefcase, Boxes, Cpu, Truck, Target, Ruler, Sparkle, ShieldCheck, Users, Globe } from "lucide-react";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const CAPABILITIES = [
  { icon: Briefcase, title: "Business", body: "We help you identify where AI can genuinely create value — and where it can't." },
  { icon: Boxes, title: "Product", body: "We design AI-powered products around real people and real decisions, not just technical possibility." },
  { icon: Cpu, title: "Technology", body: "We build with the right tools for the job — combining our own expertise with a trusted network of specialists." },
  { icon: Truck, title: "Delivery", body: "We stay close throughout — from first idea to working product, and beyond." },
];

const VALUES = [
  { icon: Target, title: "Purpose", body: "We build technology that means something — not technology for its own sake." },
  { icon: Ruler, title: "Practicality", body: "Real problems, usable solutions, measurable value. Always." },
  { icon: Sparkle, title: "Curiosity", body: "Genuinely curious about what AI can do — and honest about what it can't, yet." },
  { icon: ShieldCheck, title: "Responsibility", body: "Powerful technology deserves careful hands. We take that seriously." },
  { icon: Users, title: "Collaboration", body: "The best solutions come from working closely with the people who'll actually use them." },
  { icon: Globe, title: "Inclusion", body: "AI's benefits should reach everyone — not just those who already have access." },
];

const WhoWeAre = () => {
  return (
    <section id="who" className="relative py-28 lg:py-36 border-t border-black/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="badge-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
            Who We Are
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl leading-[1.1]">
            An <span className="text-[#43B0CE]">AI technology and advisory</span> company.
          </h2>
          <p className="mt-6 max-w-3xl text-lg font-light leading-relaxed text-[#3C5156]">
            We design and build AI-powered applications and digital products, and help businesses and individuals
            of all sizes identify where AI can genuinely make a difference. We started by providing dedicated AI
            and technology development capability — and today we bring that same practical, grounded approach to
            small and midsize businesses, because good AI shouldn't be reserved for those who can afford a large
            technical department.
          </p>
        </motion.div>

        {/* Capabilities */}
        <div className="mt-16">
          <h3 className="font-display text-2xl font-bold text-[#0B2429]">Our Capabilities</h3>
          <motion.div variants={stagger()} initial="hidden" whileInView="show" viewport={viewport} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPABILITIES.map((c) => (
              <motion.div key={c.title} variants={fadeUp} className="rounded-2xl border border-black/10 bg-[#FFFFFF] p-6">
                <span className="icon-tile">
                  <c.icon className="h-5 w-5" />
                </span>
                <h4 className="mt-5 font-display text-lg font-semibold text-[#0B2429]">{c.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#3C5156]">{c.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Values */}
        <div className="mt-20">
          <h3 className="font-display text-2xl font-bold text-[#0B2429]">Our Values</h3>
          <p className="mt-1 text-sm text-[#64787E]">Six ideas that shape how we work.</p>
          <motion.div variants={stagger(0.08)} initial="hidden" whileInView="show" viewport={viewport} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v) => (
              <motion.div key={v.title} variants={fadeUp} className="card-arch rounded-2xl p-7">
                <v.icon className="h-6 w-6 text-[#43B0CE]" />
                <h4 className="mt-5 font-display text-lg font-semibold text-[#0B2429]">{v.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#3C5156]">{v.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhoWeAre;
